<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Track } from 'livekit-client'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import VoiceAvatar from '~/components/voice-ui/VoiceAvatar/VoiceAvatar.vue'
import CallTile from './CallTile.vue'
import type { Participant } from '~/stores/groups'
import type { CallViewMode } from '~/lib/settings/appSettings'
import { usePlatform } from '~/composables/usePlatform'

const { isMobile } = usePlatform()
const gridColumnsStyle = computed(() => `repeat(auto-fill, minmax(370px, 1fr))`)

const props = defineProps<{
  participants: Participant[]
  /** Mirrors LiveKit's GridLayout/FocusLayout pair — defaults to 'grid'. */
  viewMode?: CallViewMode
  /** When false, participants with neither camera nor screen share (avatar-only tiles) are hidden entirely. */
  showParticipantsWithoutVideo?: boolean
  /**
   * Placeholder tiles to show while the call is still connecting (no participants yet) — one per
   * person already in the channel, with their avatar, so the grid comes up in its final shape.
   * See VoiceCallView.vue.
   */
  placeholders?: { id: string, name: string, avatarUrl: string }[]
}>()

const emit = defineEmits<{
  (e: 'registerVideo', el: HTMLVideoElement | null, sid: string, source: Track.Source): void
  (e: 'requestFullscreen', sid: string, source: Track.Source, name: string): void
  (e: 'showAllParticipants'): void
}>()

const screenSharers = computed(() => props.participants.filter(p => p.isScreenShareEnabled))
const peopleTiles = computed(() => props.participants.filter(p =>
  !p.isScreenShareEnabled && (props.showParticipantsWithoutVideo !== false || p.isVideoEnabled)
))

// One shared list for screen shares and camera/voice-only participants — screen shares are
// listed first so they stay visually prominent across every layout below.
const tiles = computed(() => [
  ...screenSharers.value.map(p => ({ key: p.sid, kind: 'video' as const, source: Track.Source.ScreenShare, participant: p })),
  ...peopleTiles.value.map(p => ({ key: p.sid, kind: (p.isVideoEnabled ? 'video' : 'avatar') as const, source: Track.Source.Camera, participant: p })),
])

// "Показывать участников без видео" being off hid every tile that would've been shown —
// not just no one being in the call at all — so point at the fix (turning the setting back
// on) instead of rendering a blank grid.
const allParticipantsHidden = computed(() =>
  props.showParticipantsWithoutVideo === false && props.participants.length > 0 && tiles.value.length === 0
)

// Focus view's "big" tile — mirrors LiveKit's own FocusLayoutContainer pairing: an active
// screen share always wins focus (it's the thing everyone actually needs to see), otherwise
// the current dominant speaker, otherwise just the first tile so focus mode is never empty.
const focusTile = computed(() => {
  if (tiles.value.length === 0) return null
  const screenShare = tiles.value.find(t => t.source === Track.Source.ScreenShare)
  if (screenShare) return screenShare
  const speaking = tiles.value.find(t => t.participant.isSpeaking)
  return speaking ?? tiles.value[0]
})
// Everyone else, shown as a horizontally scrolling strip alongside the focus tile.
const otherTiles = computed(() => tiles.value.filter(t => t.key !== focusTile.value?.key))

// Phone grid: one or two people get full-width 16:9 tiles stacked; from three on, two square tiles
// per row — a single column of 16:9 tiles on a ~370px-wide screen fits barely three people before
// scrolling, and avatar-only tiles don't need the width anyway.
const mobileTwoColumns = computed(() => tiles.value.length > 2)

const showPlaceholders = computed(() => tiles.value.length === 0 && (props.placeholders?.length ?? 0) > 0)
const placeholderTwoColumns = computed(() => (props.placeholders?.length ?? 0) > 2)

function placeholderInitials(name: string) {
  return name.split(' ').filter(Boolean).map(part => part[0]).join('').toUpperCase().slice(0, 2) || '?'
}

// Each video tile's own width / height (reported by CallTile once the stream's size is known). A
// phone camera is portrait, and a fixed 16:9 tile with `object-cover` cut away most of the frame —
// so on the one-column phone grid a video tile takes the stream's shape instead, no narrower than
// 3:4 so a tall 9:16 feed doesn't fill the whole screen (the rest is letterboxed, see CallTile's
// `object-contain` on mobile).
const tileAspects = ref<Record<string, number>>({})
const MIN_MOBILE_TILE_ASPECT = 3 / 4

function setTileAspect(key: string, ratio: number) {
  tileAspects.value[key] = ratio
}

function mobileTileStyle(tile: { key: string, kind: 'video' | 'avatar' }) {
  if (mobileTwoColumns.value || tile.kind !== 'video') return undefined
  const ratio = tileAspects.value[tile.key]
  if (!ratio) return undefined
  return { aspectRatio: String(Math.max(ratio, MIN_MOBILE_TILE_ASPECT)) }
}

const swappedSids = ref<Set<string>>(new Set())

watch(screenSharers, sharers => {
  const liveSids = new Set(sharers.map(p => p.sid))
  for (const sid of swappedSids.value) {
    if (!liveSids.has(sid)) swappedSids.value.delete(sid)
  }
})

function toggleSwap(sid: string) {
  if (swappedSids.value.has(sid)) swappedSids.value.delete(sid)
  else swappedSids.value.add(sid)
}

function registerVideo(el: HTMLVideoElement | null, sid: string, source: Track.Source) {
  emit('registerVideo', el, sid, source)
}

function requestFullscreen(sid: string, source: Track.Source, name: string) {
  emit('requestFullscreen', sid, source, name)
}
</script>

<template>
  <!-- Mobile: fills exactly the space the call view gives it (between the header and the bottom
       edge) instead of the desktop's fixed `100vh - 200px` guesses, which on a phone left the stage
       taller or shorter than the screen depending on the device. `pb-28` keeps the last row
       scrollable clear of the floating controls pill (72px pill + 16px gap, see CallControls.vue). -->
  <div v-if="isMobile" class="flex h-full min-h-0 w-full flex-col">
    <div v-if="allParticipantsHidden" class="flex flex-1 flex-col items-center justify-center gap-3 px-4 pb-28 text-center">
      <p class="text-sm text-primary">Участники без видео скрыты</p>
      <Button variant="default" size="sm" @click.stop="emit('showAllParticipants')">Показать всех участников</Button>
    </div>

    <!-- Connecting: one skeleton per person in the channel, laid out like the grid below. -->
    <div v-else-if="showPlaceholders" class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <div class="flex min-h-full flex-col px-4 pb-28 pt-4">
        <div class="my-auto grid gap-3" :class="placeholderTwoColumns ? 'grid-cols-2' : 'grid-cols-1'">
          <div
            v-for="person in placeholders"
            :key="person.id"
            class="flex animate-pulse items-center justify-center rounded-xl border-2 border-border bg-primary-foreground/80"
            :class="placeholderTwoColumns ? 'aspect-square' : 'aspect-video'"
          >
            <VoiceAvatar
              class="size-16 shrink-0"
              fallback-class="text-lg font-semibold"
              :src="person.avatarUrl"
              :alt="person.name"
              :fallback="placeholderInitials(person.name)"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="viewMode === 'focus'" class="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-28 pt-4">
      <div v-if="focusTile" class="min-h-0 flex-1">
        <CallTile
          :kind="focusTile.kind"
          :source="focusTile.source"
          :participant="focusTile.participant"
          :swapped="swappedSids.has(focusTile.key)"
          @register-video="registerVideo"
          @request-fullscreen="requestFullscreen"
          @toggle-swap="toggleSwap(focusTile.key)"
        />
      </div>
      <div v-if="otherTiles.length" class="-mx-4 flex h-24 shrink-0 items-center gap-3 overflow-x-auto px-4">
        <div v-for="tile in otherTiles" :key="tile.key" class="aspect-video h-full shrink-0">
          <CallTile
            :kind="tile.kind"
            :source="tile.source"
            :participant="tile.participant"
            :swapped="swappedSids.has(tile.key)"
            @register-video="registerVideo"
            @request-fullscreen="requestFullscreen"
            @toggle-swap="toggleSwap(tile.key)"
          />
        </div>
      </div>
    </div>

    <!-- Native scroller rather than ScrollArea here: its content wrapper can take `min-h-full`, so
         `my-auto` on the grid centers a short list vertically and still scrolls a long one. -->
    <div v-else class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <div class="flex min-h-full flex-col px-4 pb-28 pt-4">
        <div class="my-auto grid gap-3" :class="mobileTwoColumns ? 'grid-cols-2' : 'grid-cols-1'">
          <div
            v-for="tile in tiles"
            :key="tile.key"
            :class="mobileTwoColumns ? 'aspect-square' : 'aspect-video'"
            :style="mobileTileStyle(tile)"
          >
            <CallTile
              :kind="tile.kind"
              :source="tile.source"
              :participant="tile.participant"
              :swapped="swappedSids.has(tile.key)"
              @register-video="registerVideo"
              @request-fullscreen="requestFullscreen"
              @toggle-swap="toggleSwap(tile.key)"
              @aspect="setTileAspect(tile.key, $event)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="flex h-full w-full flex-col gap-4 px-4">
    <div v-if="allParticipantsHidden" class="flex h-[calc(100vh-200px)] flex-col items-center justify-center gap-3 rounded-xl text-center">
      <p class="text-sm text-primary">Участники без видео скрыты</p>
      <Button variant="default" size="sm" @click.stop="emit('showAllParticipants')">Показать всех участников</Button>
    </div>

    <!-- Focus: https://docs.livekit.io/reference/components/react/component/focuslayout/ paired
         with a strip for everyone else, same as LiveKit's own FocusLayoutContainer example — an
         active screen share or the current dominant speaker fills the main area. -->
    <!-- Connecting: one skeleton per person in the channel, laid out like the grid below. -->
    <ScrollArea v-else-if="showPlaceholders" class="h-[calc(100vh-200px)] rounded-xl">
      <div
        class="grid auto-rows-[minmax(20px,auto)] gap-3"
        :style="{ gridTemplateColumns: gridColumnsStyle }"
      >
        <div
          v-for="person in placeholders"
          :key="person.id"
          class="flex aspect-video animate-pulse items-center justify-center rounded-xl border-2 border-border bg-primary-foreground/80"
        >
          <VoiceAvatar
            class="size-16 shrink-0 sm:size-20"
            fallback-class="text-lg font-semibold"
            :src="person.avatarUrl"
            :alt="person.name"
            :fallback="placeholderInitials(person.name)"
          />
        </div>
      </div>
    </ScrollArea>

    <div v-else-if="viewMode === 'focus'" class="flex h-[calc(100vh-200px)] flex-col gap-3">
      <div v-if="focusTile" class="min-h-0 flex-1">
        <CallTile
          :kind="focusTile.kind"
          :source="focusTile.source"
          :participant="focusTile.participant"
          :swapped="swappedSids.has(focusTile.key)"
          @register-video="registerVideo"
          @request-fullscreen="requestFullscreen"
          @toggle-swap="toggleSwap(focusTile.key)"
        />
      </div>
      <div v-if="otherTiles.length" class="flex h-28 shrink-0 items-center gap-3 overflow-x-auto sm:h-32">
        <div v-for="tile in otherTiles" :key="tile.key" class="aspect-video h-full shrink-0">
          <CallTile
            :kind="tile.kind"
            :source="tile.source"
            :participant="tile.participant"
            :swapped="swappedSids.has(tile.key)"
            @register-video="registerVideo"
            @request-fullscreen="requestFullscreen"
            @toggle-swap="toggleSwap(tile.key)"
          />
        </div>
      </div>
    </div>

    <!-- Grid: https://docs.livekit.io/reference/components/react/component/gridlayout/ — every
         tile wraps and scrolls together in an adaptive grid. -->
    <ScrollArea v-else class="h-[calc(100vh-200px)] rounded-xl">
      <!-- `auto-rows-*` must resolve each row from its own content, not `1fr` — with `1fr` the
           grid (stretched to the scroll area's full height) splits leftover vertical space
           equally across every row regardless of what's actually in it, instead of each row
           sizing to its own `aspect-video` content. -->
      <div class="flex flex-col">
        <div
          class="grid auto-rows-[minmax(20px,auto)] gap-3"
          :style="{ gridTemplateColumns: gridColumnsStyle }"
        >
          <div
            v-for="tile in tiles"
            :key="tile.key"
            class="aspect-video"
          >
            <CallTile
              :kind="tile.kind"
              :source="tile.source"
              :participant="tile.participant"
              :swapped="swappedSids.has(tile.key)"
              @register-video="registerVideo"
              @request-fullscreen="requestFullscreen"
              @toggle-swap="toggleSwap(tile.key)"
            />
          </div>
        </div>
      </div>
    </ScrollArea>
  </div>
</template>
